'use client'

/**
 * <ErrorDisplay /> — single error-shaped wrapper around Alert with a
 * "More details" disclosure. Per
 * `apps/agent/plans/plan-error-display-standard.md`.
 *
 * Three variants:
 *  - inline (default): drops into a form/composer/message footer
 *  - toast            : routed via useErrorToast (sonner)
 *  - card             : full-surface for page-level failures
 *
 * Use `toUiError(thrown, { title, hint })` to wrap any thrown Error or
 * domain error class into the `UiError` shape this component takes.
 */

'use client'

import { AlertCircle, ChevronDown, ChevronRight, Copy, Loader2, X } from 'lucide-react'
import * as React from 'react'
import { toast } from 'sonner'

import { cn } from '../cn'
import { Alert } from './alert'
import { Button } from './button'

export interface UiError {
  /** One sentence the user reads. Imperative. ≤ 80 chars. */
  title: string
  /** Optional second line — what to do next. ≤ 120 chars. */
  hint?: string
  /** Stable machine code — `s3-upload.cors`, `auth.session-expired`, … */
  code?: string
  /** Free-form technical detail. Only shown after "More details" expand. */
  details?: string
  /** OpenTelemetry trace id when available. */
  traceId?: string
  /** Where the error originated, surfaced in the copy-to-clipboard payload. */
  source?: string
  /** Optional retry handler — when set, shows a Retry button. */
  onRetry?: () => void | Promise<void>
}

/**
 * Type-guard for callers that already produce a UiError-shaped object
 * (avoids re-wrapping).
 */
export function isUiError(x: unknown): x is UiError {
  return !!x && typeof x === 'object' && typeof (x as { title?: unknown }).title === 'string'
}

/**
 * Wrap any thrown value into a UiError. Pass overrides for the title/hint
 * so callers can keep the user-facing copy in their own scope.
 */
export function toUiError(err: unknown, fallback: Partial<UiError> = {}): UiError {
  if (isUiError(err)) {
    return { ...err, ...fallback, title: fallback.title ?? err.title, hint: fallback.hint ?? err.hint }
  }
  if (typeof err === 'string') {
    return { title: fallback.title ?? err, hint: fallback.hint, ...fallback }
  }
  if (err instanceof Error) {
    // Detect a few common error-class shapes without importing them — keeps
    // @ordo/ui framework-agnostic.
    const errAny = err as Error & { reason?: string; status?: number; code?: string }
    return {
      title: fallback.title ?? 'Something went wrong',
      hint: fallback.hint ?? 'Try again, or contact support if this keeps happening.',
      code: fallback.code ?? errAny.code ?? errAny.reason,
      details: fallback.details ?? `${err.name}: ${err.message}${errAny.status ? ` (HTTP ${errAny.status})` : ''}`,
      source: fallback.source,
      traceId: fallback.traceId,
      onRetry: fallback.onRetry,
    }
  }
  return {
    title: fallback.title ?? 'Something went wrong',
    hint: fallback.hint ?? 'Try again, or contact support if this keeps happening.',
    details: fallback.details ?? String(err),
    ...fallback,
  }
}

interface ErrorDisplayProps {
  error: UiError
  /** inline (default), toast, or card. */
  variant?: 'inline' | 'card'
  onDismiss?: () => void
  className?: string
}

const TITLE_MAX = 80

function truncate(s: string, max: number): string {
  if (s.length <= max) {
    return s
  }
  return s.slice(0, max - 1).trimEnd() + '…'
}

export function ErrorDisplay({
  error,
  variant = 'inline',
  onDismiss,
  className,
}: ErrorDisplayProps): React.JSX.Element {
  const [expanded, setExpanded] = React.useState(false)
  const [retrying, setRetrying] = React.useState(false)

  const hasDetails = !!(error.details || error.code || error.traceId)

  const onCopy = React.useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) {
      return
    }
    const payload = JSON.stringify(
      {
        title: error.title,
        code: error.code,
        source: error.source,
        traceId: error.traceId,
        details: error.details,
      },
      null,
      2,
    )
    void navigator.clipboard.writeText(payload)
  }, [error])

  const onRetryClick = React.useCallback(async () => {
    if (!error.onRetry) {
      return
    }
    setRetrying(true)
    try {
      await error.onRetry()
    } finally {
      setRetrying(false)
    }
  }, [error])

  const Chevron = expanded ? ChevronDown : ChevronRight

  return (
    <Alert
      variant="error"
      className={cn('flex flex-col gap-1.5', variant === 'card' && 'p-5 text-base', className)}
      role="alert"
      aria-live="polite">
      <div className="flex items-start gap-2">
        <AlertCircle className={cn('mt-0.5 h-4 w-4 shrink-0', variant === 'card' && 'h-5 w-5')} aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="break-words font-medium">{truncate(error.title, TITLE_MAX)}</div>
          {error.hint ? <div className="mt-0.5 text-[13px] opacity-90">{error.hint}</div> : null}
        </div>
        {onDismiss ? (
          <button
            type="button"
            onClick={onDismiss}
            className="-mr-1 -mt-1 rounded p-1 opacity-70 transition-opacity hover:opacity-100"
            aria-label="Dismiss">
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        ) : null}
      </div>

      {error.onRetry || hasDetails ? (
        <div className="flex flex-wrap items-center gap-2 pl-6">
          {error.onRetry ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onRetryClick}
              disabled={retrying}
              className="h-7 px-2 text-xs">
              {retrying ? <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden /> : null}
              Retry
            </Button>
          ) : null}
          {hasDetails ? (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex items-center gap-1 text-[11px] font-medium opacity-80 transition-opacity hover:opacity-100">
              <Chevron className="h-3 w-3" aria-hidden />
              More details
            </button>
          ) : null}
          {expanded && hasDetails ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onCopy}
              className="h-7 px-2 text-[11px]"
              title="Copy details to clipboard">
              <Copy className="mr-1 h-3 w-3" aria-hidden />
              Copy
            </Button>
          ) : null}
        </div>
      ) : null}

      {expanded && hasDetails ? (
        <div className="ml-6 rounded-md border border-current/20 bg-background/60 p-2 font-mono text-[11px] leading-relaxed text-foreground">
          {error.code ? (
            <div>
              <span className="opacity-60">Code:</span> {error.code}
            </div>
          ) : null}
          {error.source ? (
            <div>
              <span className="opacity-60">Source:</span> {error.source}
            </div>
          ) : null}
          {error.traceId ? (
            <div>
              <span className="opacity-60">Trace:</span> <span className="break-all">{error.traceId}</span>
            </div>
          ) : null}
          {error.details ? (
            <div
              className={cn(
                'break-words whitespace-pre-wrap',
                (error.code || error.source || error.traceId) && 'mt-2',
              )}>
              {error.details}
            </div>
          ) : null}
        </div>
      ) : null}
    </Alert>
  )
}

/**
 * useErrorToast — single import for callers who don't want to plumb
 * <ErrorDisplay /> state. Renders the same component inside a sonner toast.
 *
 *   const { showError } = useErrorToast()
 *   showError(toUiError(err, { title: "Couldn't upload that file" }))
 */
export function useErrorToast() {
  const showError = React.useCallback((error: UiError): void => {
    toast.custom(
      (t) => <ErrorDisplay error={error} variant="inline" onDismiss={() => toast.dismiss(t)} className="shadow-lg" />,
      { duration: 8000 },
    )
  }, [])
  return { showError }
}
